import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Calendar, MessageCircle, Phone } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../components/ui/Button';
const FAQS = [{
  question: 'Do I need to book an appointment or can I walk in?',
  answer: 'Patients are encouraged to schedule appointments in advance to minimize wait times. However, walk-ins are welcome and will be accommodated depending on availability and the urgency of the medical condition.'
}, {
  question: 'What should I bring to my first appointment?',
  answer: 'Please bring your national ID or passport, any referral letters, your medical history, a list of current medications, and your medical aid/insurance information if applicable.'
}, {
  question: 'Which medical aids do you accept?',
  answer: 'We accept most major medical aid schemes in Botswana including Pula Medical Aid, Botsogo Health Plan, BPOMAS, and BOMAID. Please confirm your specific plan with our reception desk before your visit.'
}, {
  question: 'How long does it take to get laboratory results?',
  answer: 'Turnaround times vary depending on the specific test. Many routine tests are processed on the same day in our in-house laboratory, while specialized tests may take 24-48 hours.'
}, {
  question: 'Do you offer emergency services?',
  answer: 'We provide urgent care for minor emergencies during our operating hours. For life-threatening emergencies, please proceed to the nearest hospital emergency department.'
}, {
  question: 'What are your operating hours?',
  answer: 'We are open Monday to Friday from 8:00 AM to 6:00 PM and Saturday from 9:00 AM to 1:00 PM. We are closed on Sundays and public holidays.'
}, {
  question: 'Can I book an appointment on WhatsApp?',
  answer: 'Yes. You can reach us on WhatsApp at +267 73241613 to request an appointment, and our team will confirm your preferred date and time.'
}, {
  question: 'Are my medical records kept confidential?',
  answer: 'Absolutely. All patient records are handled with the strictest confidentiality and are only accessible to authorized medical personnel involved in your care.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const whatsappLink = `https://wa.me/26773241613?text=${encodeURIComponent('Hello Maisha Medical Center. I have a question I would like to ask.')}`;
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-756" data-mp="3037a2kok34ra">
      {/* Hero Band */}
      <div className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-757" data-mp="3014lm531xjoo">
        <div className="absolute -top-[30%] -right-[10%] w-[60%] h-[140%] rounded-full bg-brand-red/5 blur-[120px] pointer-events-none" data-id="element-758" data-mp="2v5fi5qnwvin3" />
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-759" data-mp="15r0yvlvnwnyz">
          <nav aria-label="Breadcrumb" className="flex items-center justify-center gap-2 text-sm text-slate-300 mb-6" data-id="element-760" data-mp="10sfynmgeu4aj">
            <Link to="/" className="hover:text-brand-gold transition-colors" data-id="element-761" data-mp="3hbi3cf6qa4zl">
              Home
            </Link>
            <ChevronRight className="w-4 h-4" data-id="element-762" data-mp="32n3yxztny8gx" />
            <span className="text-brand-gold" data-id="element-763" data-mp="37mgncp06nnlm">FAQ</span>
          </nav>
          <span className="text-brand-gold font-semibold tracking-wider text-sm uppercase mb-4 block" data-id="element-764" data-mp="37kncexijeh7t">
            Common Questions
          </span>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-765" data-mp="2xosnxlrmtrx4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-766" data-mp="2jqm2x67s7mev">
            Find answers to the most common questions about appointments,
            services, medical aid, and what to expect at Maisha Medical Center.
          </p>
        </div>
      </div>

      {/* Accordion */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-id="element-767" data-mp="mvptjwfcev5x">
        <div className="space-y-4" data-id="element-768" data-mp="2iychbudgnuy0">
          {FAQS.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return <div key={idx} className={twMerge('bg-white border rounded-2xl overflow-hidden transition-all duration-300', isOpen ? 'border-brand-navy shadow-soft' : 'border-slate-200 hover:border-brand-navy/30')} data-id="element-769" data-mp="2dyq5npda6mrb" data-mpi={idx}>
                <button onClick={() => setOpenIndex(isOpen ? null : idx)} aria-expanded={isOpen} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy/30 rounded-2xl" data-id="element-770" data-mp="1v99qbvwxspqu">
                  <span className="font-heading font-semibold text-lg text-brand-navy pr-8" data-id="element-771" data-mp="1hcd0nxsk1ebj">
                    {faq.question}
                  </span>
                  <ChevronDown className={twMerge('w-5 h-5 text-brand-red shrink-0 transition-transform duration-300', isOpen ? 'rotate-180' : '')} data-id="element-772" data-mp="3nzc0ve0jt4ah" />
                </button>
                <div className={twMerge('px-6 overflow-hidden transition-all duration-300 ease-in-out', isOpen ? 'max-h-96 pb-5 opacity-100' : 'max-h-0 opacity-0')} data-id="element-773" data-mp="5snwxrh8in6e">
                  <p className="text-slate-600 leading-relaxed" data-id="element-774" data-mp="3a51jxiusmeez">{faq.answer}</p>
                </div>
              </div>;
        })}
        </div>

        {/* CTA Band */}
        <div className="mt-16 bg-brand-navy rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-775" data-mp="3j10q912fipr7">
          <div className="absolute -bottom-[40%] -left-[10%] w-[50%] h-[150%] rounded-full bg-brand-gold/10 blur-[100px] pointer-events-none" data-id="element-776" data-mp="2mhba7h0jtkty" />
          <div className="relative z-10" data-id="element-777" data-mp="3ttspxj4x5nbq">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3" data-id="element-778" data-mp="3tshovr2uytob">
              Still have questions?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto" data-id="element-779" data-mp="7ehstc7g44we">
              Our friendly team is happy to help. Reach out, book an
              appointment, or chat with us directly on WhatsApp.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-780" data-mp="3auolknobohqe">
              <Button href="/book" variant="primary" icon={<Calendar className="w-5 h-5" data-id="element-782" data-mp="35ygb3nl2lcpk" />} data-id="element-781" data-mp="35w3kkm18xu0w">
                Book Appointment
              </Button>
              <Button href={whatsappLink} target="_blank" variant="white" icon={<MessageCircle className="w-5 h-5 text-[#25D366]" data-id="element-784" data-mp="z4mct9rampx3" />} data-id="element-783" data-mp="3fujbanwd319v">
                Chat on WhatsApp
              </Button>
              <Button href="/contact" variant="outline" icon={<Phone className="w-5 h-5" data-id="element-786" data-mp="12po4a4tlnh9d" />} className="border-white/40 text-white hover:bg-white hover:text-brand-navy" data-id="element-785" data-mp="v651ee0uat9v">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}